import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, KeyRound, Copy, Users, Power, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

const field = "h-11 rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400";
const EMPTY = { full_name: "", email: "", password: "" };
const PAGE_SIZE = 20;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const initials = (name = "") => name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const AddMember = ({ onAdded }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/members", f);
      setF(EMPTY);
      toast.success(`Member created · License ${data.license_number}`);
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="admin-add-member-form">
      <h3 className="font-display flex items-center gap-2 text-lg font-bold text-slate-900"><UserPlus className="h-5 w-5 text-blue-600" /> Add a member</h3>
      <p className="mt-1 text-sm text-slate-600">A unique software license number is generated automatically.</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <Input required placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className={field} data-testid="admin-member-name-input" />
        <Input required type="email" placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="admin-member-email-input" />
        <Input required type="text" placeholder="Set a password (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={field} data-testid="admin-member-password-input" />
      </div>
      <button disabled={busy} data-testid="admin-member-create-button" className="font-display mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-bold uppercase text-white disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Create member
      </button>
    </form>
  );
};

const MemberCard = ({ m, onChange }) => {
  const [showPw, setShowPw] = useState(false);
  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPwd = () => {
    const pwd = window.prompt(`Set a new password for ${m.full_name}:`);
    if (pwd) run(() => api.patch(`/admin/members/${m.id}`, { password: pwd }), "Password updated");
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`admin-member-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-blue-100" data-testid={`admin-member-avatar-${m.id}`}>
            {m.avatar_url ? (
              <img src={`${BACKEND}${m.avatar_url}`} alt={m.full_name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-sm font-black text-blue-600">{initials(m.full_name)}</div>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-display font-bold text-slate-900">{m.full_name}</p>
              <span className={`font-mono-x rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${m.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`admin-member-status-${m.id}`}>{m.active ? "Active" : "Disabled"}</span>
            </div>
            <p className="truncate text-sm text-slate-600">{m.email}</p>
            <button onClick={() => { navigator.clipboard?.writeText(m.license_number); toast.success("License copied"); }} className="font-mono-x mt-2 inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700" data-testid={`admin-member-license-${m.id}`}>
              <KeyRound className="h-3.5 w-3.5" /> {m.license_number} <Copy className="h-3 w-3" />
            </button>
            <div className="mt-2 flex items-center gap-2" data-testid={`admin-member-password-row-${m.id}`}>
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Password</span>
              <span className="font-mono-x rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-800" data-testid={`admin-member-password-${m.id}`}>
                {m.password ? (showPw ? m.password : "•".repeat(Math.min(m.password.length, 10))) : "— (set via Reset)"}
              </span>
              {m.password && (
                <>
                  <button onClick={() => setShowPw((v) => !v)} data-testid={`admin-member-password-toggle-${m.id}`} className="text-slate-400 hover:text-blue-600" aria-label={showPw ? "Hide password" : "Show password"} title={showPw ? "Hide" : "Show"}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button onClick={() => { navigator.clipboard?.writeText(m.password); toast.success("Password copied"); }} data-testid={`admin-member-password-copy-${m.id}`} className="text-slate-400 hover:text-blue-600" aria-label="Copy password" title="Copy"><Copy className="h-3.5 w-3.5" /></button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => run(() => api.patch(`/admin/members/${m.id}`, { active: !m.active }), m.active ? "Member disabled" : "Member enabled")} data-testid={`admin-member-toggle-${m.id}`} className={`rounded-xl border p-2.5 ${m.active ? "border-slate-300 text-slate-600 hover:border-amber-400 hover:text-amber-600" : "border-emerald-300 text-emerald-600"}`} aria-label="Toggle active" title={m.active ? "Disable login" : "Enable login"}><Power className="h-4 w-4" /></button>
          <button onClick={resetPwd} data-testid={`admin-member-resetpw-${m.id}`} className="rounded-xl border border-slate-300 p-2.5 text-slate-600 hover:border-blue-400 hover:text-blue-600" aria-label="Reset password" title="Reset password"><KeyRound className="h-4 w-4" /></button>
          <button onClick={() => window.confirm(`Delete ${m.full_name}? This cannot be undone.`) && run(() => api.delete(`/admin/members/${m.id}`), "Member deleted")} data-testid={`admin-member-delete-${m.id}`} className="rounded-xl border border-slate-300 p-2.5 text-slate-600 hover:border-red-400 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export const MemberManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/members");
      setMembers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(members.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = members.length > PAGE_SIZE ? members.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE) : members;
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  return (
    <div className="space-y-6" data-testid="admin-member-manager">
      <AddMember onAdded={() => { load(); setPage(1); }} />
      <div>
        <h3 className="font-display flex items-center gap-2 text-lg font-bold text-slate-900"><Users className="h-5 w-5 text-blue-600" /> Members ({members.length})</h3>
        <p className="mt-1 text-sm text-slate-600">Only members listed here can log in. Disable to block a login without deleting.</p>
        <div className="mt-5 grid gap-3 lg:grid-cols-2" data-testid="admin-member-list">
          {loading ? (
            <div className="col-span-full flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : members.length === 0 ? (
            <p className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="admin-no-members">No members yet. Add your first member above.</p>
          ) : (
            paginated.map((m) => <MemberCard key={m.id} m={m} onChange={load} />)
          )}
        </div>
        {members.length > PAGE_SIZE && (
          <div className="mt-6 flex items-center justify-center gap-3" data-testid="admin-member-pagination">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} data-testid="admin-member-prev-page" className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-900 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Prev</button>
            <span className="text-sm font-semibold text-slate-600" data-testid="admin-member-page-indicator">Page {safePage} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} data-testid="admin-member-next-page" className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-900 disabled:opacity-40">Next <ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
};
